import { useMemo, useEffect, useRef, useCallback } from 'react';
import { useQuery } from 'react-query';
import { Modal } from '@fortress-technology-solutions/fortress-component-library/Molecules_Fortress';
import { Form } from '@fortress-technology-solutions/fortress-component-library/Organisms';
import { Typography } from '@fortress-technology-solutions/fortress-component-library/Atoms';
import {
  AddIcon,
  PencilIcon,
} from '@fortress-technology-solutions/fortress-component-library/Icons';
import { formatDateDB } from '@fortress-technology-solutions/fortress-component-library/utils';
import moment from 'moment';
import useReactHookFormProps from '../../../../hooks/useReactHookFormProps';
import HudVoucherService from '../../../../services/hudVoucherService';
import { getFormFields } from './constants';
import messages from './messages';

function toWholeNumber(value) {
  if (value == null || value === '') return undefined;
  const n = Number(value);
  if (Number.isNaN(n) || !Number.isFinite(n)) return undefined;
  return Math.round(n);
}

function toFlooredRetained(value) {
  if (value == null || value === '') return undefined;
  const n = Number(value);
  if (Number.isNaN(n) || !Number.isFinite(n)) return undefined;
  return Math.floor(n);
}

function buildPayload(data) {
  const agreementType =
    data.agreementType === 'T'
      ? 'TENANT'
      : data.agreementType === 'O'
      ? 'OWNER'
      : 'NONE';
  const newOrOngoing = data.newOrOngoing === 'ONGOING' ? 'ONGOING' : 'NEW';

  let unitId;
  let householdId;
  const unitResident = data.unitResident;
  if (unitResident && typeof unitResident === 'object') {
    unitId = unitResident.unitId;
    householdId = unitResident.householdId;
  } else if (typeof unitResident === 'string' && unitResident.includes('|')) {
    const [parsedUnitId, parsedHouseholdId] = unitResident.split('|');
    unitId = parsedUnitId?.trim() || undefined;
    householdId = parsedHouseholdId?.trim() || undefined;
  }

  let agreementDate = data.agreementDate;
  if (agreementDate) {
    const m =
      typeof agreementDate.toISOString === 'function'
        ? moment(agreementDate)
        : moment(agreementDate, 'YYYY-MM-DD');
    agreementDate = m.isValid() ? formatDateDB(m) : undefined;
  }

  const agreementAmount = toWholeNumber(data.agreementAmount);
  const totalPayment = toWholeNumber(data.totalPayment) ?? 0;
  const amountRetained = toFlooredRetained(data.amountRetained);

  const payload = {
    agreementType,
    newOrOngoing,
    agreementDate,
    agreementAmount,
    totalPayment,
  };

  if (unitId && householdId) {
    payload.unitId = unitId;
    payload.householdId = householdId;
  }
  if (data.householdStatus) payload.householdStatus = data.householdStatus;

  if (newOrOngoing === 'ONGOING') {
    payload.beginningBalance = toWholeNumber(data.beginningBalance);
    payload.agreementChangeAmount = toWholeNumber(data.agreementChangeAmount);
    payload.agreementId = String(data.agreementId ?? '').trim();
  }

  if (agreementType === 'TENANT' && amountRetained != null)
    payload.amountRetained = amountRetained;

  return payload;
}

function buildModifyPayload(
  data,
  { agreementChangeAmountOverridden = false } = {},
) {
  const agreementType =
    data.agreementType === 'T'
      ? 'TENANT'
      : data.agreementType === 'O'
      ? 'OWNER'
      : 'NONE';

  const agreementAmount = toWholeNumber(data.agreementAmount);
  const totalPayment =
    agreementType === 'NONE' ? 0 : toWholeNumber(data.totalPayment) ?? 0;
  const agreementChangeAmount = toWholeNumber(data.agreementChangeAmount) ?? 0;
  const amountRetainedRaw = toFlooredRetained(data.amountRetained);

  const payload = {
    agreementAmount,
    totalPayment,
    agreementChangeAmount,
  };

  // The server derives the change amount unless this flag says the user typed
  // their own. Sent only when true so an unedited request stays as it was.
  if (agreementChangeAmountOverridden) {
    payload.agreementChangeAmountOverridden = true;
  }

  if (agreementType === 'TENANT' && amountRetainedRaw != null) {
    payload.amountRetained = amountRetainedRaw;
  }

  return payload;
}

// Ending Balance = Beginning Balance + Agreement Change Amount - Total Payment.
// Mirrors the server's computeRepaymentBalances helper so the preview shown here
// cannot disagree with what gets stored.
function computeEndingBalance({
  beginningBalance,
  agreementChangeAmount,
  totalPayment,
}) {
  const beg = Number(beginningBalance ?? 0);
  const change = Number(agreementChangeAmount ?? 0);
  const total = Number(totalPayment ?? 0);
  return beg + change - total;
}

// How long to wait after the last keystroke before recalculating the change
// amount.
const CHANGE_AMOUNT_SETTLE_MS = 400;

// The Agreement Amount as it ARRIVED on this voucher. Carry-over zeroes the
// change amount each month, so on an untouched row this is just the displayed
// amount. Same reconstruction the server does, so both sides agree on the base.
function carriedInAgreementAmount(initialValues) {
  return (
    Number(initialValues?.agreementAmount ?? 0) -
    Number(initialValues?.agreementChangeAmount ?? 0)
  );
}
function AddRepaymentAgreementModal({
  open,
  intl,
  organizationId,
  propertyId,
  voucherId,
  voucherMonthMoment,
  isSubmitting = false,
  onClose,
  onSubmit,
  title: titleProp,
  description: descriptionProp,
  isModifying = false,
  initialValues,
}) {
  const hudVoucherService = useMemo(() => new HudVoucherService(), []);

  const defaultValues = useMemo(
    () => ({
      agreementType: '',
      newOrOngoing: '',
      householdStatus: '',
      unitResident: '',
      agreementDate: null,
      agreementAmount: undefined,
      beginningBalance: undefined,
      totalPayment: undefined,
      agreementChangeAmount: undefined,
      endingBalance: undefined,
      amountRetained: undefined,
      amountRequested: undefined,
      agreementId: '',
    }),
    [],
  );

  const reactHookFormProps = useReactHookFormProps({
    defaultValues,
    mode: 'onChange',
  });

  const formRef = useRef(reactHookFormProps);
  formRef.current = reactHookFormProps;

  useEffect(() => {
    if (!open || !isModifying || !initialValues) return;
    formRef.current.reset({
      ...defaultValues,
      ...initialValues,
    });
  }, [open, isModifying, initialValues, defaultValues]);

  const agreementType = reactHookFormProps.watch('agreementType');
  const newOrOngoing = reactHookFormProps.watch('newOrOngoing');
  const householdStatus = reactHookFormProps.watch('householdStatus');
  const agreementAmount = reactHookFormProps.watch('agreementAmount');
  const beginningBalance = reactHookFormProps.watch('beginningBalance');
  const agreementChangeAmount = reactHookFormProps.watch(
    'agreementChangeAmount',
  );
  const totalPayment = reactHookFormProps.watch('totalPayment');
  const amountRetained = reactHookFormProps.watch('amountRetained');
  const endingBalance = reactHookFormProps.watch('endingBalance');

  const isHouseholdStatusSet =
    householdStatus === 'CURRENT_RESIDENT' ||
    householdStatus === 'PRIOR_RESIDENT';

  const { data: contractUnitsResponse } = useQuery(
    ['hudVoucherContractUnits', organizationId, propertyId, voucherId],
    () =>
      hudVoucherService.getHudVoucherContractUnits(
        organizationId,
        propertyId,
        voucherId,
      ),
    {
      enabled: Boolean(
        open &&
          organizationId &&
          propertyId &&
          voucherId &&
          isHouseholdStatusSet,
      ),
      staleTime: 60 * 1000,
    },
  );

  const contractUnits = useMemo(() => {
    return Array.isArray(contractUnitsResponse?.units)
      ? contractUnitsResponse.units
      : [];
  }, [contractUnitsResponse]);

  const unitResidentOptions = useMemo(() => {
    if (!isHouseholdStatusSet) {
      return [];
    }

    const vacantLabel = intl.formatMessage(messages.vacantOptionLabel);

    return contractUnits.map((item) => {
      const residentBucket =
        householdStatus === 'CURRENT_RESIDENT'
          ? item.currentResident
          : item.priorResident;

      const householdId = residentBucket?.householdId ?? '';
      const residentName = [
        residentBucket?.headOfHousehold?.firstName,
        residentBucket?.headOfHousehold?.lastName,
      ]
        .filter(Boolean)
        .join(' ')
        .trim();

      const unitLabel = item.number ?? '';
      const residentLabel = residentName || vacantLabel;

      return {
        value: `${item.unitId}|${householdId}`,
        text: `${unitLabel} - ${residentLabel}`,
        disabled: !householdId,
      };
    });
  }, [contractUnits, householdStatus, intl, isHouseholdStatusSet]);

  const unitResidentValue = reactHookFormProps.watch('unitResident');

  useEffect(() => {
    if (!open || isModifying) return;
    formRef.current.setValue('unitResident', '');
  }, [open, householdStatus, isModifying]);

  const validUnitResidentValues = useMemo(
    () => new Set(unitResidentOptions.map((o) => o.value)),
    [unitResidentOptions],
  );

  useEffect(() => {
    if (!open) return;
    if (
      unitResidentValue != null &&
      unitResidentValue !== '' &&
      !validUnitResidentValues.has(unitResidentValue)
    ) {
      formRef.current.setValue('unitResident', '');
    }
  }, [open, unitResidentValue, validUnitResidentValues]);

  useEffect(() => {
    if (!open || isModifying) return;
    if (newOrOngoing === 'NEW') {
      const amount = toWholeNumber(agreementAmount);
      const value = amount != null && amount > 0 ? amount : undefined;
      // A brand-new agreement always opens at zero -- it isn't derived from the
      // agreement amount, so there's no invalid-amount case to guard against here.
      formRef.current.setValue('beginningBalance', 0);
      formRef.current.setValue('agreementChangeAmount', value);
    }
  }, [open, newOrOngoing, agreementAmount, isModifying]);

  useEffect(() => {
    if (!open) return;
    if (agreementType === 'N') {
      formRef.current.setValue('totalPayment', 0);
    }
  }, [open, agreementType]);

  useEffect(() => {
    // In modify mode the beginning balance is handed in and never edited, so use
    // it directly. This used to back-derive it as
    // endingBalance + totalPayment - agreementChangeAmount, which only held while
    // the stored row satisfied that invariant -- and stored rows did not, which
    // is what made the preview disagree with the server (ENG-26976).
    const ending = computeEndingBalance({
      beginningBalance: isModifying
        ? initialValues?.beginningBalance
        : beginningBalance,
      agreementChangeAmount:
        agreementChangeAmount != null
          ? agreementChangeAmount
          : initialValues?.agreementChangeAmount,
      totalPayment:
        totalPayment != null ? totalPayment : initialValues?.totalPayment,
    });
    formRef.current.setValue('endingBalance', Math.round(ending), {
      shouldValidate: true,
    });
  }, [
    beginningBalance,
    agreementChangeAmount,
    totalPayment,
    isModifying,
    initialValues,
  ]);

  // ENG-26976: editing the Agreement Amount auto-fills the Agreement Change
  // Amount with the difference from the amount carried into this voucher.
  //
  // Waits for typing to settle before writing. The form runs mode: 'onChange',
  // so an unbounded version of this effect fired on every keystroke and walked
  // the field through the partial numbers: editing 1000 -> 1100 passes through
  // "100", which reads as a -900 change. Only the settled value is ever written.
  //
  // The cost is a short window where Ending Balance still reflects the previous
  // change amount, so Save could be enabled against a stale balance. That window
  // closes as soon as the user moves focus toward Save, and the server rejects a
  // negative ending balance independently, so it is the better trade than showing
  // wrong money mid-keystroke.
  //
  // Stops once the user types their own value in the change field (AC3).
  //
  // react-hook-form's dirtyFields is NOT usable as that signal here. setValue
  // without shouldDirty does not mark a field dirty in react-hook-form itself,
  // but the component library's currency input echoes a programmatic write back
  // through field.onChange, which react-hook-form treats as user input. The
  // auto-fill therefore marked itself as an override and never ran again after
  // the first edit.
  //
  // So track it directly: remember the value this effect last wrote, and treat
  // any other value appearing in the field as the user's own. Seeded from
  // initialValues so an edit made before the first auto-fill is respected too.
  const lastAutoFilledChangeRef = useRef(null);
  const changeOverriddenRef = useRef(false);

  useEffect(() => {
    if (!open) {
      lastAutoFilledChangeRef.current = null;
      changeOverriddenRef.current = false;
      return;
    }
    if (isModifying && initialValues) {
      lastAutoFilledChangeRef.current =
        toWholeNumber(initialValues.agreementChangeAmount) ?? 0;
      changeOverriddenRef.current = false;
    }
  }, [open, isModifying, initialValues]);

  useEffect(() => {
    if (!open || !isModifying) return;
    if (lastAutoFilledChangeRef.current == null) return;
    const current = toWholeNumber(agreementChangeAmount);
    if (current == null) return;
    if (current !== lastAutoFilledChangeRef.current) {
      changeOverriddenRef.current = true;
    }
  }, [open, isModifying, agreementChangeAmount]);

  useEffect(() => {
    if (!open || !isModifying || !initialValues) return undefined;
    if (changeOverriddenRef.current) return undefined;
    const amount = toWholeNumber(agreementAmount);
    if (amount == null) return undefined;
    const next = amount - carriedInAgreementAmount(initialValues);
    const id = setTimeout(() => {
      if (changeOverriddenRef.current) return;
      // Record before writing so the detector above recognises this value as
      // ours rather than as a user edit.
      lastAutoFilledChangeRef.current = next;
      formRef.current.setValue('agreementChangeAmount', next, {
        shouldValidate: true,
      });
    }, CHANGE_AMOUNT_SETTLE_MS);
    return () => clearTimeout(id);
  }, [open, isModifying, initialValues, agreementAmount]);

  useEffect(() => {
    const change = Number(agreementChangeAmount ?? 0);
    const total = Number(totalPayment ?? 0);
    const rawRetained = Number(amountRetained ?? 0);
    const retained = Number.isFinite(rawRetained) ? Math.floor(rawRetained) : 0;
    const requested = change - total + retained;
    formRef.current.setValue('amountRequested', Math.round(requested));
  }, [agreementChangeAmount, totalPayment, amountRetained]);

  const totalEditedRef = useRef(false);

  useEffect(() => {
    if (!open) {
      totalEditedRef.current = false;
      return;
    }
    if (agreementType !== 'T') {
      formRef.current.setValue('amountRetained', undefined);
      return;
    }
    const total = Number(totalPayment ?? 0);
    // On Modify the saved total maps to the stored value: kept as is on open,
    // put back if the user edits Total Payment and then returns to it.
    if (isModifying && total === Number(initialValues?.totalPayment ?? 0)) {
      if (totalEditedRef.current) {
        formRef.current.setValue(
          'amountRetained',
          initialValues?.amountRetained,
          { shouldValidate: true },
        );
      }
      return;
    }
    totalEditedRef.current = true;
    // Rounded down to match the server's maxRetainedFor.
    // Synchronous on purpose: a delayed write let Save send a half-typed value.
    const defaultRetained = total >= 0 ? Math.floor(total * 0.2) : undefined;
    formRef.current.setValue('amountRetained', defaultRetained, {
      shouldValidate: true,
    });
  }, [open, isModifying, initialValues, agreementType, totalPayment]);

  const handleClose = useCallback(() => {
    onClose();
    formRef.current.reset(defaultValues);
  }, [onClose, defaultValues]);

  useEffect(() => {
    if (!open) {
      formRef.current.reset(defaultValues);
    }
  }, [open, defaultValues]);

  const handleSubmit = useCallback(
    async (data) => {
      const ending = Number(data.endingBalance ?? 0);
      if (ending < 0) {
        formRef.current.setError('endingBalance', {
          type: 'manual',
          message: intl.formatMessage(messages.endingBalanceNegativeError),
        });
        return;
      }
      const payload = isModifying
        ? buildModifyPayload(data, {
            agreementChangeAmountOverridden: changeOverriddenRef.current,
          })
        : buildPayload(data);
      await onSubmit(payload);
      handleClose();
    },
    [intl, isModifying, onSubmit, handleClose],
  );

  const title =
    titleProp ??
    intl.formatMessage(
      isModifying ? messages.modalTitleModify : messages.modalTitle,
    );
  const description =
    descriptionProp ??
    intl.formatMessage(
      isModifying ? messages.modalDescriptionModify : messages.modalDescription,
    );

  const formFields = useMemo(
    () =>
      getFormFields(messages, intl, {
        unitResidentOptions,
        voucherMonthMoment,
        isModifying,
      }),
    [intl, unitResidentOptions, voucherMonthMoment, isModifying],
  );

  const hasNegativeEndingBalance =
    typeof endingBalance === 'number' && endingBalance < 0;
  const isSaveDisabled =
    !reactHookFormProps.isValid || hasNegativeEndingBalance;

  return (
    <Modal
      open={open}
      onClose={handleClose}
      icon={isModifying ? <PencilIcon /> : <AddIcon />}
      title={title}
      ModalProps={{
        PaperProps: {
          sx: {
            width: {
              xs: 'calc(100% - 32px)',
              sm: 560,
            },
            maxHeight: '90vh',
            overflow: 'auto',
          },
        },
      }}
      actionsProps={[
        {
          children: intl.formatMessage(messages.cancel),
          onClick: handleClose,
        },
        {
          children: intl.formatMessage(messages.save),
          submitButton: true,
          isSubmitting: isSubmitting || reactHookFormProps.isSubmitting,
          disabled: isSaveDisabled,
          onClick: reactHookFormProps.handleSubmit(handleSubmit),
        },
      ]}
    >
      <Typography variant="bodyMedium" marginBottom={1.5} display="block">
        {description}
      </Typography>
      <Form ReactHookFormProps={reactHookFormProps} fields={formFields} />
    </Modal>
  );
}

export default AddRepaymentAgreementModal;
